/** screen_tour_details.xml, the document half.
 *
 * The real screen is a full-bleed Google Map with a draggable bottom sheet over it.
 * This renders the SHEET at its half-expanded resting height (0.6 of the viewport) with
 * a neutral panel where the map goes — the map layer is a separate phase, gated on a
 * spike, and drawing a fake one would be worse than drawing none.
 *
 * Spec §1A, §4.4, §4.5.
 */
import { useEffect, useState } from 'react';

import { adaptTour } from './adapters/tour';
import { Toolbar } from './chrome/Chrome';
import { TourItemView, type TourItemContext } from './items/TourItems';
import { mapTourDetails } from './mappers/tourDetails';
import { imageWidthPx } from '../../deviceProfiles';
import type { ScreenProps } from '../../types';

/** BetterBottomSheetBehavior: halfExpandedRatio 0.6, so the sheet takes the bottom 60%. */
export const HALF_EXPANDED_RATIO = 0.6;
/** Spec §7: sheet peek is 150px, which is where the map button puts it. */
export const COLLAPSED_PEEK_PX = 150;

/** When the map is real, the screen no longer owns its own day/selection state: the map
 *  lives in the parent document (a separate React root) and the two must agree. The
 *  shell lifts that state and passes it down here. See `../index.tsx`. */
export interface TourMapBinding {
  dayIndex: number;
  onDayIndex: (index: number) => void;
  selectedActivityId: number | null;
  sheetCollapsed: boolean;
  onToggleSheet: () => void;
}

export function TourScreen({
  payload,
  device,
  imagesBase,
  map,
  dayIndex: wanted,
}: ScreenProps & { map?: TourMapBinding; /** The editor's selected day. */ dayIndex?: number }) {
  const tour = adaptTour(payload);
  const [ownDayIndex, setOwnDayIndex] = useState(wanted ?? 0);
  useEffect(() => {
    if (wanted !== undefined) setOwnDayIndex(wanted);
  }, [wanted]);
  const [expandedSlots, setExpandedSlots] = useState<Set<number>>(new Set());

  const dayIndex = map ? map.dayIndex : ownDayIndex;
  const setDayIndex = map ? map.onDayIndex : setOwnDayIndex;

  const ui = mapTourDetails(tour, { currentDayIndex: dayIndex, expandedSlots });
  const ctx: TourItemContext = { imagesBase, imageWidthPx: imageWidthPx(device) };

  const toggleSlot = (slotId: number) =>
    setExpandedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId);
      else next.add(slotId);
      return next;
    });

  const sheetTop = map?.sheetCollapsed
    ? device.heightDp - COLLAPSED_PEEK_PX
    : Math.round(device.heightDp * (1 - HALF_EXPANDED_RATIO));

  return (
    <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {/* Where the map is. With a real map underlaid in the parent document this region
          is a hole: transparent, and click-through so drags and marker taps reach it. */}
      {map ? (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--and-color-surface)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: device.statusBarDp + 80,
          }}
        >
          <div
            className="and-type-caption-dark-medium"
            style={{ textAlign: 'center', maxWidth: 260 }}
          >
            Map not shown in preview
          </div>
        </div>
      )}

      <Toolbar title={ui.title} statusBarDp={device.statusBarDp} alpha={0} />

      {/* uikit_map_button: floats over the map, 20dp above the sheet. Tapping it does NOT
          open anything — upstream it scrolls the list to top and drops the sheet to
          COLLAPSED, i.e. "let me see the map". */}
      {map && (
        <button
          type="button"
          onClick={map.onToggleSheet}
          aria-label={map.sheetCollapsed ? 'Show the itinerary' : 'Show the map'}
          style={{
            position: 'absolute',
            right: 20,
            top: sheetTop - 40 - 20,
            width: 40,
            height: 40,
            borderRadius: 20,
            border: 'none',
            background: 'var(--and-color-white)',
            boxShadow: '0 2px 6px rgb(0 0 0 / 0.24)',
            cursor: 'pointer',
            pointerEvents: 'auto',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          {map.sheetCollapsed ? '\u2261' : '\u25B2'}
        </button>
      )}

      {/* bg_uikit_bottom_sheet: white, 24dp top corners */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: sheetTop,
          bottom: 0,
          background: 'var(--and-color-white)',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -2px 8px rgb(0 0 0 / 0.12)',
        }}
      >
        {/* BottomSheetHeaderView: 34dp tall, a 56x4dp grabber at 17dp */}
        <div style={{ height: 34, display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              marginTop: 17,
              width: 56,
              height: 4,
              borderRadius: 2,
              background: 'var(--and-color-gray)',
            }}
          />
        </div>

        {/* uikit_layout_tab.xml — 34dp pills, radius 17dp, 8dp between */}
        {ui.tabs.length > 1 ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: '10px 8px',
              overflowX: 'auto',
              justifyContent: 'center',
            }}
          >
            {ui.tabs.map((tab) => {
              const selected = tab.id === ui.currentDayIndex;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setDayIndex(tab.id)}
                  style={{
                    flex: '0 0 auto',
                    height: 34,
                    padding: '0 12px',
                    borderRadius: 17,
                    fontSize: 12,
                    fontWeight: 400,
                    cursor: 'pointer',
                    background: selected ? 'var(--and-color-blue)' : 'var(--and-color-white)',
                    color: selected ? 'var(--and-color-white)' : 'var(--and-color-color-dark-medium)',
                    border: selected ? 'none' : '1px solid var(--and-color-divider)',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : (
          ui.tabs[0] && (
            <div
              className="and-type-caption-dark-medium"
              style={{ height: 34, margin: '10px 0', textAlign: 'center' }}
            >
              {ui.tabs[0].label}
            </div>
          )
        )}

        <div style={{ flex: 1, overflowY: 'auto', paddingTop: 4, paddingBottom: 80 + device.navBarDp }}>
          {ui.items.map((item) => (
            <TourItemView key={item.key} item={item} ctx={ctx} onToggleSlot={toggleSlot} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function tourScreenNotes(payload: Record<string, unknown>): string[] {
  return mapTourDetails(adaptTour(payload)).notes;
}
